/**
 * Run-scoped read tools for the multi-hop executor agent.
 *
 * These tools wrap the exported helpers from `excelService.ts`
 * (`listWorksheetsMetadata`, `readRangeContent`) which in turn use the
 * proven Bridge adapters (`adaptWorkbookSheets`, `adaptRangeInfo`, CSV
 * fallback). They are pure reads with no side effects on the workbook —
 * safe to call at any point in the agent loop.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  listWorksheetsMetadata,
  readRangeContent,
  type WorksheetMetadata,
} from "./excelService";
import { isAbortError } from "../shared/utils/abortError";

// ---------------------------------------------------------------------------
// list_sheets
// ---------------------------------------------------------------------------

export const LIST_SHEETS_TOOL: ChartCapabilityToolDef = {
  name: "list_sheets",
  description:
    "Returns worksheet metadata (names, visibility, used-range bounds, sheetType) for the " +
    "active or pinned workbook. Sheet identity is the tab name — there is no stable sheet id; " +
    "index is tab order only and changes if sheets are reordered. Call again after rename_sheet " +
    "(or any rename) before further reads/writes; names are not remapped. When sheetType is " +
    "present, do not read_range chart sheets (sheetType other than xlWorksheet). " +
    "usedRange is the sheet's real data rectangle and may not start at A1 (e.g. D5:F10); " +
    "usedRange: null means the host confirmed the sheet is empty, so skip it; a missing " +
    "usedRange field means the extent is unknown, not that the sheet is empty — read_range " +
    "it before concluding anything. Does NOT return cell values — use read_range for that.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workbookName: {
        type: ["string", "null"],
        description:
          "Target workbook name. Pass null to use the active/pinned workbook.",
      },
    },
    required: ["workbookName"],
    additionalProperties: false,
  },
};

/** Validated input for `list_sheets`. */
export interface ListSheetsInput {
  /** Workbook name or null for active/pinned workbook. */
  workbookName: string | null;
}

/** One sheet entry in the `list_sheets` result. */
export interface ListSheetsSheetEntry {
  /** Tab name. This is the only sheet identity; it does not survive rename. */
  name: string;
  /** Tab-order position (host-dependent 0- or 1-based). Not an identity. */
  index: number;
  /**
   * Excel-native visibility (`Visible` / `Hidden` / `VeryHidden`).
   * Host COM constants (`xlSheetVisible`, `xlSheetHidden`,
   * `xlSheetVeryHidden`) are mapped here. Omitted when the host did
   * not send a recognised value — unknown, not Visible.
   */
  visibility?: "Visible" | "Hidden" | "VeryHidden";
  /**
   * Used-range bounds.
   * - object: parseable used range
   * - `null`: host confirmed blank
   * - omitted: host did not send usedRange (unknown, not blank)
   */
  usedRange?: {
    /** Bare A1 rectangle (e.g. "D5:F10"). */
    address: string;
    /** Number of rows in that rectangle. */
    rowCount: number;
    /** Number of columns in that rectangle. */
    columnCount: number;
  } | null;
  /** Host sheet kind (`xlWorksheet`, `xlChart`, …). Absent when omitted on the wire. */
  sheetType?: string;
}

/** Full result shape returned by `list_sheets`. */
export interface ListSheetsResult {
  /** Ordered list of sheets with metadata. */
  sheets: ListSheetsSheetEntry[];
  /** Error description if the call failed. */
  error?: string;
}

/**
 * Executes the `list_sheets` tool. Delegates to
 * `listWorksheetsMetadata`, which parses Bridge/Office.js payload
 * variants and distinguishes a genuine empty workbook from a
 * malformed host response.
 *
 * @param rawInput - Parsed JSON arguments from the LLM's function_call
 *   output. Expected shape: `{ workbookName: string | null }`.
 * @returns JSON string conforming to {@link ListSheetsResult}. On
 *   a host/shape failure, returns `{ sheets: [], error }` so the
 *   agent can retry instead of treating the workbook as empty.
 *   `AbortError` is rethrown, not converted into that empty result.
 */
export async function executeListSheetsTool(
  rawInput: unknown,
): Promise<string> {
  try {
    const input = normaliseListSheetsInput(rawInput);
    const sheets = await listWorksheetsMetadata(
      input.workbookName ?? undefined,
    );

    const result: ListSheetsResult = {
      sheets: sheets.map(projectSheetMetadata),
    };
    return JSON.stringify(result);
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    return JSON.stringify({
      sheets: [],
      error: `list_sheets failed: ${err instanceof Error ? err.message : String(err)}`,
    } satisfies ListSheetsResult);
  }
}
/**
 * Normalises raw LLM arguments into a typed {@link ListSheetsInput}.
 * Handles missing/malformed fields gracefully by falling back to null
 * (active workbook), so the downstream call always receives valid input.
 *
 * @param raw - Parsed JSON from the model (may be null or non-object).
 * @returns Normalised input with `workbookName` guaranteed to be
 *   `string | null`.
 */
function normaliseListSheetsInput(raw: unknown): ListSheetsInput {
  if (raw === null || typeof raw !== "object") return { workbookName: null };
  const r = raw as Record<string, unknown>;
  return {
    workbookName:
      typeof r.workbookName === "string" && r.workbookName.trim()
        ? r.workbookName
        : null,
  };
}

/**
 * Projects internal {@link WorksheetMetadata} into the public-facing
 * {@link ListSheetsSheetEntry} shape. Omits optional fields when absent
 * to keep the JSON payload compact for the model's context window.
 *
 * @param s - Adapted worksheet metadata from `listWorksheetsMetadata`.
 * @returns Projected sheet entry for the tool result.
 */
function projectSheetMetadata(s: WorksheetMetadata): ListSheetsSheetEntry {
  const entry: ListSheetsSheetEntry = {
    name: s.name,
    index: s.index,
  };
  if (s.visibility) {
    entry.visibility = s.visibility;
  }
  if (s.sheetType) {
    entry.sheetType = s.sheetType;
  }
  if (s.usedRange) {
    entry.usedRange = {
      address: s.usedRange.address,
      rowCount: s.usedRange.rowCount,
      columnCount: s.usedRange.columnCount,
    };
  } else if (s.usedRange === null) {
    entry.usedRange = null;
  }
  return entry;
}

// ---------------------------------------------------------------------------
// read_range
// ---------------------------------------------------------------------------

export const READ_RANGE_TOOL: ChartCapabilityToolDef = {
  name: "read_range",
  description:
    "Reads values, formulas, and/or format metadata from a specific worksheet " +
    "range. Call after list_sheets. Prefer targeted reads — use the sheet's " +
    "usedRange bounds to pick an address. Use again after writes to verify. " +
    "Valid address strings: a single cell (A1), a rectangle (A1:G80), the same " +
    "with $ ($A$1:$G$80), and columns past Z (AA1:AB20). Pass null to read the " +
    "sheet's used range (may not start at A1). Do not send whole-column A:A, " +
    "whole-row 1:1, named ranges, R1C1, or an empty string — those are not " +
    "rectangles and return an error. " +
    "DEFAULT to format 'csv' (compact, values only) for plain value/label reads " +
    "and value verification — it is much cheaper on context. Only use format " +
    "'json' with includeFormulas:true when you need to read or verify formula " +
    "TEXT, or includeFormats:true when you need number formats / cell styles. " +
    "includeFormulas and includeFormats apply only in format 'json'; CSV mode " +
    "returns values only. " +
    "The returned address is the requested rectangle (empty cells are null, not omitted). " +
    "emptyRange:true means the host confirmed a blank range — skip it. An error " +
    "means the read failed; do not treat that as empty. truncated:true means the " +
    "20k-cell ceiling clipped rows, including on emptyRange; page rather than " +
    "assume the rest is empty. " +
    "warnings csv_fallback_used means values came from GetRangeAsCsv, not GetRangeInfo; " +
    "quoted commas stay one cell. formulasAvailable is true only when formula TEXT " +
    "is present in the grid — displayed values are not formulas. " +
    "formulas_unavailable means formula TEXT was requested but the host did not " +
    "return a recognised formula list; an empty list means the cells are confirmed " +
    "non-formulas, not unavailable.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: {
        type: "string",
        description:
          "Worksheet tab name from list_sheets. Required; whitespace-only is missing.",
      },
      address: {
        type: ["string", "null"],
        description:
          "A1 rectangle (e.g. 'A1:G80') or null for the sheet's used range (may not start at A1). Empty string is invalid.",
      },
      workbookName: {
        type: ["string", "null"],
        description:
          "Target workbook name. Pass null for the active/pinned workbook.",
      },
      format: {
        type: "string",
        enum: ["json", "csv"],
        description:
          "Context-budget projection. 'csv' is compact values only; 'json' is required for formula TEXT or formats.",
      },
      includeFormulas: {
        type: "boolean",
        description:
          "When true and format is 'json', include formula TEXT for each cell. Displayed values are not formulas.",
      },
      includeFormats: {
        type: "boolean",
        description:
          "When true and format is 'json', include number formats and cell styles.",
      },
    },
    required: [
      "sheetName",
      "address",
      "workbookName",
      "format",
      "includeFormulas",
      "includeFormats",
    ],
    additionalProperties: false,
  },
};

/** Validated input for `read_range`. */
export interface ReadRangeInput {
  /** Worksheet name to read from. */
  sheetName: string;
  /** A1 rectangle, or null for the sheet's used range (may not start at A1). */
  address: string | null;
  /** Workbook name or null for active/pinned. */
  workbookName: string | null;
  /** Output format: structured JSON or compact CSV. */
  format: "json" | "csv";
  /** Include formula TEXT in JSON mode. */
  includeFormulas: boolean;
  /** Include number format metadata in JSON mode. */
  includeFormats: boolean;
}

/** Full result shape returned by `read_range`. */
export interface ReadRangeResult {
  /** Sheet that was read. */
  sheetName: string;
  /** Caller address, or the used-range address when `address` was null. */
  requestedAddress?: string;
  /** Rectangle actually returned. Matches the request unless `truncated` clipped it. */
  address: string;
  /** Format used for this result. */
  format: "json" | "csv";
  /** 0-based row offset of the returned range in the worksheet. */
  startRow?: number;
  /** 0-based column offset of the returned range in the worksheet. */
  startColumn?: number;
  /** Rows in the result. */
  rowCount: number;
  /** Columns in the result. */
  columnCount: number;
  /** True when formulas were requested from the host. */
  formulasRequested?: boolean;
  /** True when formula TEXT was actually present in the returned grid. */
  formulasAvailable?: boolean;
  /** True when format metadata was requested from the host. */
  formatsRequested?: boolean;
  /** True when number formats were actually returned by the host/read path. */
  numberFormatsAvailable?: boolean;
  /** True when sparse cell styles were actually returned by the host/read path. */
  cellStylesAvailable?: boolean;
  /** 2D cell values (JSON mode). */
  values?: (string | number | boolean | null)[][];
  /** 2D formula strings (JSON mode, when requested). */
  formulas?: (string | null)[][];
  /** 2D number format codes (JSON mode, when requested). */
  numberFormats?: (string | null)[][];
  /** Sparse style metadata keyed by "row,col" within the returned range. */
  cellStyles?: Record<string, unknown>;
  /** Raw CSV string (CSV mode). */
  csv?: string;
  /**
   * True when the 20k-cell ceiling clipped rows from the request.
   * Can be true together with `emptyRange` — a clipped blank is still
   * truncated; page rather than treating the rest of the sheet as empty.
   */
  truncated?: boolean;
  /**
   * True when the host confirmed a blank range. Distinct from `error`
   * (failed read, including an unusable GetRangeInfo success payload).
   */
  emptyRange?: boolean;
  /** Host evidence path used for JSON values. */
  evidenceSource?: "rich" | "csv_fallback" | "rich_blank";
  /**
   * Observation caveats. `csv_fallback_used` means values came from
   * GetRangeAsCsv, not GetRangeInfo. `formulas_unavailable` means
   * formula TEXT was requested but ParseFormulas did not return a
   * recognised list; a recognised empty list is confirmed non-formulas
   * and does not set this warning.
   */
  warnings?: string[];
  /** Error description if the call failed. */
  error?: string;
}

/**
 * Executes the `read_range` tool. JSON and CSV both go through
 * `readRangeContent` so pad, clip, RFC 4180 CSV fallback, and the
 * blank-versus-failed distinction are the same in both formats. CSV
 * mode then serializes the capped values via `valuesToCsv`.
 *
 * When `address` is null, `readRangeContent` resolves the sheet's
 * used-range address via `listWorksheetsMetadata`.
 *
 * @param rawInput - Parsed JSON arguments from the LLM's function_call
 *   output. Expected shape: {@link ReadRangeInput}.
 * @returns JSON string conforming to {@link ReadRangeResult}. On
 *   failure, returns `{ rowCount: 0, columnCount: 0, error }` so the
 *   agent can retry instead of treating the range as empty.
 *   `AbortError` is rethrown, not converted into that empty result.
 */
export async function executeReadRangeTool(
  rawInput: unknown,
): Promise<string> {
  try {
    const input = normaliseReadRangeInput(rawInput);

    if (!input.sheetName.trim()) {
      return JSON.stringify({
        sheetName: "",
        address: "",
        format: input.format,
        rowCount: 0,
        columnCount: 0,
        error: "Missing required parameter: sheetName",
      } satisfies ReadRangeResult);
    }
    if (input.format === "csv") {
      return await executeCsvRead(input);
    }
    return await executeJsonRead(input);
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    const input = normaliseReadRangeInput(rawInput);
    return JSON.stringify({
      sheetName: input.sheetName,
      address: input.address ?? "",
      format: input.format,
      rowCount: 0,
      columnCount: 0,
      error: `read_range failed: ${err instanceof Error ? err.message : String(err)}`,
    } satisfies ReadRangeResult);
  }
}

/**
 * JSON path: same `readRangeContent` observation as CSV, then project
 * formula TEXT / formats. `formulasAvailable` is true only when a
 * non-empty formula string is present — an empty formulas grid is not
 * "available". A confirmed blank range sets `emptyRange`; a missing
 * range with no such flag is a failed read.
 */
async function executeJsonRead(input: ReadRangeInput): Promise<string> {
  const { range, truncated, emptyRange, evidenceSource, warnings } =
    await readRangeContent(
      input.sheetName,
      input.address,
      {
        includeFormulas: input.includeFormulas,
        includeFormats: input.includeFormats,
      },
      input.workbookName ?? undefined,
    );

  if (!range) {
    if (emptyRange) {
      return JSON.stringify({
        sheetName: input.sheetName,
        requestedAddress: input.address ?? "(blank)",
        address: input.address ?? "(blank)",
        format: "json",
        rowCount: 0,
        columnCount: 0,
        emptyRange: true,
        formulasRequested: input.includeFormulas,
        formulasAvailable: false,
        formatsRequested: input.includeFormats,
      } satisfies ReadRangeResult);
    }
    throw new Error(
      `read_range returned no range for "${input.sheetName}" ${input.address ?? "(blank)"}`,
    );
  }

  const result: ReadRangeResult = {
    sheetName: input.sheetName,
    requestedAddress: input.address ?? range.address,
    address: range.address,
    format: "json",
    startRow: range.startRow,
    startColumn: range.startColumn,
    rowCount: range.rowCount,
    columnCount: range.columnCount,
    formulasRequested: input.includeFormulas,
    formulasAvailable: Boolean(
      input.includeFormulas && hasFormulaText(range.formulas),
    ),
    formatsRequested: input.includeFormats,
    numberFormatsAvailable: Boolean(input.includeFormats && range.numberFormats),
    cellStylesAvailable: Boolean(input.includeFormats && range.cellStyles),
    values: range.values,
    evidenceSource,
  };

  if (input.includeFormulas && range.formulas) {
    result.formulas = range.formulas;
  }
  if (input.includeFormats && range.numberFormats) {
    result.numberFormats = range.numberFormats;
  }
  if (input.includeFormats && range.cellStyles) {
    result.cellStyles = range.cellStyles;
  }
  if (truncated) {
    result.truncated = true;
  }
  if (emptyRange) {
    result.emptyRange = true;
  }
  if (warnings && warnings.length > 0) {
    result.warnings = warnings;
  }

  return JSON.stringify(result);
}

/**
 * CSV path: same `readRangeContent` observation as JSON (pad, clip,
 * RFC 4180 fallback), then `valuesToCsv`. Truncation and
 * `csv_fallback_used` therefore match JSON mode.
 */
async function executeCsvRead(input: ReadRangeInput): Promise<string> {
  const { range, truncated, emptyRange, warnings } = await readRangeContent(
    input.sheetName,
    input.address,
    { includeFormulas: false, includeFormats: false },
    input.workbookName ?? undefined,
  );

  if (!range) {
    if (emptyRange) {
      return JSON.stringify({
        sheetName: input.sheetName,
        requestedAddress: input.address ?? "(blank)",
        address: input.address ?? "(blank)",
        format: "csv",
        csv: "",
        rowCount: 0,
        columnCount: 0,
        emptyRange: true,
      } satisfies ReadRangeResult);
    }
    throw new Error(
      `read_range returned no range for "${input.sheetName}" ${input.address ?? "(blank)"}`,
    );
  }

  const csv = valuesToCsv(range.values);

  const result: ReadRangeResult = {
    sheetName: input.sheetName,
    requestedAddress: input.address ?? range.address,
    address: range.address,
    format: "csv",
    csv,
    rowCount: range.rowCount,
    columnCount: range.columnCount,
  };
  if (truncated) {
    result.truncated = true;
  }
  if (emptyRange) {
    result.emptyRange = true;
  }
  if (warnings && warnings.length > 0) {
    result.warnings = warnings;
  }
  return JSON.stringify(result);
}

function hasFormulaText(
  formulas: (string | null)[][] | undefined,
): boolean {
  if (!formulas) return false;
  for (const row of formulas) {
    for (const cell of row) {
      if (typeof cell === "string" && cell.length > 0) return true;
    }
  }
  return false;
}

/**
 * Serializes a 2D values array into a CSV string. Handles quoting for
 * values containing commas, double-quotes, or newlines per RFC 4180.
 *
 * @param values - 2D array of cell values from a `CellRange`.
 * @returns RFC 4180 CSV string with CRLF line endings.
 */
function valuesToCsv(values: (string | number | boolean | null)[][]): string {
  return values.map((row) =>
    row.map((cell) => {
      if (cell === null || cell === undefined) return "";
      const str = String(cell);
      if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(","),
  ).join("\r\n");
}

/**
 * Normalises raw LLM arguments into a typed {@link ReadRangeInput}.
 * Handles missing/malformed fields gracefully with compact-by-default values:
 * - `sheetName` → empty string (caught by validation in `executeReadRangeTool`)
 * - `address` → null (resolves to used range)
 * - `workbookName` → null (active/pinned)
 * - `format` → "csv" (compact value-only read; the model opts into "json")
 * - `includeFormulas` / `includeFormats` → only honoured in json mode, and
 *   default to false so a read never silently pays for formula/format payloads
 *   the caller didn't ask for. The model must explicitly opt in.
 *
 * Note: `read_range` is a strict tool, so the model normally supplies every
 * field; these defaults only apply to malformed/partial input.
 *
 * @param raw - Parsed JSON from the model (may be null or non-object).
 * @returns Normalised input safe for downstream consumption.
 */
function normaliseReadRangeInput(raw: unknown): ReadRangeInput {
  if (raw === null || typeof raw !== "object") {
    return {
      sheetName: "",
      address: null,
      workbookName: null,
      format: "csv",
      includeFormulas: false,
      includeFormats: false,
    };
  }
  const r = raw as Record<string, unknown>;
  return {
    sheetName: typeof r.sheetName === "string" ? r.sheetName : "",
    address: typeof r.address === "string" ? r.address : null,
    workbookName:
      typeof r.workbookName === "string" && r.workbookName.trim()
        ? r.workbookName
        : null,
    format: r.format === "json" ? "json" : "csv",
    includeFormulas: r.includeFormulas === true,
    includeFormats: r.includeFormats === true,
  };
}

// ---------------------------------------------------------------------------
// Aggregate exports
// ---------------------------------------------------------------------------

export const EXECUTOR_READ_TOOLS: readonly ChartCapabilityToolDef[] = [
  LIST_SHEETS_TOOL,
  READ_RANGE_TOOL,
];
